import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth, API, getActiveAccountHeader } from '../App';
import { useSubscription, ReadOnlyBanner } from '../contexts/SubscriptionContext';
import { AddToHomeScreenPrompt } from './AddToHomeScreenPrompt';
import { AccountSwitcher, useActiveAccount } from './AccountSwitcher';
import { NotificationBell } from './NotificationBell';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Home, 
  LayoutGrid, 
  Bell, 
  Package, 
  FileText, 
  User, 
  Menu, 
  X,
  LogOut,
  ChevronRight,
  FileSignature,
  Users,
  UsersRound,
  DollarSign,
  ClipboardList,
  Send,
  Target,
  Building2,
  Crown,
  HelpCircle
} from 'lucide-react';

const getNavItems = (isPro) => {
  const items = [
    { path: '/dashboard', label: 'Home', icon: Home },
    { path: '/horses', label: 'Horses', icon: LayoutGrid },
  ];
  
  // Add Stable Dashboard for Pro users
  if (isPro) {
    items.push({ path: '/stable-dashboard', label: 'Stable Dashboard', icon: Building2, isPro: true });
  }
  
  items.push(
    { path: '/reminders', label: 'Reminders', icon: Bell },
    { path: '/goals', label: 'Goals', icon: Target },
    { path: '/expenses', label: 'Finance Vault', icon: DollarSign },
    { path: '/gear', label: 'Tack', icon: Package },
    { path: '/documents', label: 'Documents', icon: FileText },
    { path: '/contracts', label: 'Contracts', icon: FileSignature },
    { path: '/reports', label: 'Reports', icon: ClipboardList },
    { path: '/rider-profiles', label: 'Rider Profiles', icon: Users },
    { path: '/team', label: 'Team', icon: UsersRound },
    { path: '/account', label: 'Account', icon: User },
    { path: '/help', label: 'Help & FAQ', icon: HelpCircle }
  );
  
  return items;
};

export const Layout = ({ children }) => {
  const location = useLocation();
  const { user, logout, token } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [reminderCount, setReminderCount] = useState(0);
  const [pendingTransfers, setPendingTransfers] = useState(0);
  
  // Check if user is Pro for nav items
  const isPro = user?.subscription_plan === 'pro';
  const navItems = getNavItems(isPro);

  // Fetch reminder count and pending transfers
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!token) return;
      try {
        const headers = { 
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${token}`,
          ...getActiveAccountHeader()
        };
        
        // Fetch reminders
        const remindersRes = await fetch(`${API}/dashboard/reminders`, {  headers });
        if (remindersRes.ok) {
          const data = await remindersRes.json();
          const today = new Date();
          const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
          const urgentReminders = (data.upcoming_health || []).filter(r => {
            if (!r.next_due_date) return false;
            const dueDate = new Date(r.next_due_date);
            return dueDate <= nextWeek;
          });
          setReminderCount(urgentReminders.length);
        }

        // Fetch pending transfers
        const transfersRes = await fetch(`${API}/transfers`, {  headers });
        if (transfersRes.ok) {
          const transfers = await transfersRes.json();
          const pending = transfers.filter(t => t.status === 'pending' && t.to_user_email === user?.email);
          setPendingTransfers(pending.length);
        }
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
      }
    };

    fetchNotifications();
    const interval = setInterval(fetchNotifications, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [token, user?.email]);

  const totalNotifications = reminderCount + pendingTransfers;

  const handleLogout = async () => {
    await logout();
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-bone">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-1 bg-white border-r border-muted">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-muted">
            <Link to="/dashboard" className="flex items-center gap-2">
              <img src="/logo.png" alt="EquiVault" className="h-8 w-auto" />
              <span className="text-xl font-bold font-serif text-forest">EquiVault</span>
            </Link>
            {/* Desktop notification bell */}
            <Link to="/reminders" className="relative" data-testid="desktop-notification-bell">
              <Button variant="ghost" size="icon" className="h-9 w-9">
                <Bell className="w-5 h-5 text-muted-foreground" />
              </Button>
              {totalNotifications > 0 && (
                <span className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center text-xs font-bold text-white bg-destructive rounded-full">
                  {totalNotifications > 9 ? '9+' : totalNotifications}
                </span>
              )}
            </Link>
            {pendingTransfers > 0 && (
              <Link to="/dashboard" className="relative" data-testid="desktop-transfer-notification">
                <Button variant="ghost" size="icon" className="h-9 w-9">
                  <Send className="w-5 h-5 text-forest" />
                </Button>
                <span className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center text-xs font-bold text-white bg-forest rounded-full">
                  {pendingTransfers}
                </span>
              </Link>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {/* Account Switcher - shown when user has multiple accounts */}
            <div className="mb-4 flex items-center gap-2">
              <div className="flex-1">
                <AccountSwitcher />
              </div>
              <NotificationBell />
            </div>
            {navItems.map((item) => {
              const isActive = location.pathname === item.path || 
                (item.path === '/horses' && location.pathname.startsWith('/horses'));
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={cn(
                    'sidebar-link',
                    isActive && 'active'
                  )}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                  {item.isPro && (
                    <Badge className="ml-auto bg-forest text-white text-xs px-1.5 py-0">
                      <Crown className="w-3 h-3" />
                    </Badge>
                  )}
                  {item.path === '/reminders' && reminderCount > 0 && (
                    <span className="ml-auto h-5 w-5 flex items-center justify-center text-xs font-bold text-white bg-destructive rounded-full">
                      {reminderCount > 9 ? '9+' : reminderCount}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-muted">
            <div className="flex items-center gap-3 mb-3">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-forest flex items-center justify-center text-white font-medium">
                  {user?.name?.charAt(0) || 'U'}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              className="w-full justify-start text-muted-foreground hover:text-destructive"
              onClick={handleLogout}
              data-testid="sidebar-logout"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-40 h-14 bg-white border-b border-muted flex items-center justify-between px-4">
        <div className="flex items-center">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-6 h-6" />
          </Button>
          <Link to="/dashboard" className="flex items-center gap-2 ml-3">
            <img src="/logo.png" alt="EquiVault" className="h-7 w-auto" />
            <span className="font-bold font-serif text-forest">EquiVault</span>
          </Link>
        </div>
        {/* Mobile notification bell */}
        <div className="flex items-center gap-1">
          <NotificationBell />
          <Link to="/reminders" className="relative" data-testid="mobile-reminders-link">
            <Button variant="ghost" size="icon" className="h-9 w-9">
              <Bell className="w-5 h-5 text-muted-foreground" />
            </Button>
            {reminderCount > 0 && (
              <span className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center text-xs font-bold text-white bg-destructive rounded-full">
                {reminderCount > 9 ? '9+' : reminderCount}
              </span>
            )}
          </Link>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-40 bg-black/50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside className={cn(
        'lg:hidden fixed inset-y-0 left-0 z-50 w-72 bg-white transform transition-transform duration-300 ease-in-out flex flex-col',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      )}>
        <div className="flex items-center justify-between h-14 px-4 border-b border-muted flex-shrink-0">
          <Link to="/dashboard" className="flex items-center gap-2">
            <img src="/logo.png" alt="EquiVault" className="h-7 w-auto" />
            <span className="font-bold font-serif text-forest">EquiVault</span>
          </Link>
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(false)}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <nav className="flex-1 overflow-y-auto p-4 space-y-1">
          {/* Account Switcher for mobile */}
          <div className="mb-4">
            <AccountSwitcher />
          </div>
          {navItems.map((item) => {
            const isActive = location.pathname === item.path ||
              (item.path === '/horses' && location.pathname.startsWith('/horses'));
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={cn(
                  'sidebar-link',
                  isActive && 'active'
                )}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
                {item.path === '/reminders' && reminderCount > 0 ? (
                  <span className="ml-auto h-5 w-5 flex items-center justify-center text-xs font-bold text-white bg-destructive rounded-full">
                    {reminderCount > 9 ? '9+' : reminderCount}
                  </span>
                ) : (
                  <ChevronRight className="w-4 h-4 ml-auto opacity-50" />
                )}
              </Link>
            );
          })}
        </nav>

        <div className="flex-shrink-0 p-4 border-t border-muted bg-white">
          <div className="flex items-center gap-3 mb-3">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-forest flex items-center justify-center text-white font-medium">
                {user?.name?.charAt(0) || 'U'}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            className="w-full justify-start text-muted-foreground hover:text-destructive"
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Mobile Bottom Nav */}
      <nav className="lg:hidden bottom-nav">
        <div className="flex justify-around items-center h-14">
          {navItems.slice(0, 4).map((item) => {
            const isActive = location.pathname === item.path ||
              (item.path === '/horses' && location.pathname.startsWith('/horses'));
            return (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  'bottom-nav-item flex flex-col items-center gap-0.5 p-2 relative',
                  isActive && 'active'
                )}
                data-testid={`bottom-nav-${item.label.toLowerCase()}`}
              >
                <div className="relative">
                  <item.icon className={cn('w-5 h-5', isActive ? 'text-forest' : 'text-muted-foreground')} />
                  {item.path === '/reminders' && reminderCount > 0 && (
                    <span className="absolute -top-2 -right-2 h-4 w-4 flex items-center justify-center text-[10px] font-bold text-white bg-destructive rounded-full">
                      {reminderCount > 9 ? '9+' : reminderCount}
                    </span>
                  )}
                </div>
                <span className={cn('text-xs', isActive ? 'text-forest font-medium' : 'text-muted-foreground')}>
                  {item.label}
                </span>
              </Link>
            );
          })}
          {/* Menu button for iOS users */}
          <button
            onClick={() => setSidebarOpen(true)}
            className="bottom-nav-item flex flex-col items-center gap-0.5 p-2"
            data-testid="bottom-nav-menu"
          >
            <Menu className="w-5 h-5 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">More</span>
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="lg:pl-64 pt-14 lg:pt-0 pb-16 lg:pb-0 min-h-screen">
        <ReadOnlyBanner />
        {children}
      </main>
      
      {/* Mobile Add to Home Screen Prompt */}
      <AddToHomeScreenPrompt />
    </div>
  );
};
